import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CloudDownload, Loader2, RotateCcw } from "lucide-react";

/**
 * SnapshotsDialog — lists the current user's cloud snapshots (Emergent
 * Object Store) and lets them restore any of them as a new job. Snapshots
 * are auto-created on job save (throttled server-side to 1/24h/job).
 */
export const SnapshotsDialog = ({ open, onOpenChange, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/snapshots");
        setSnapshots(r.data?.snapshots || []);
      } catch (err) {
        toast.error("Could not load snapshots");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const restore = async (id) => {
    setRestoringId(id);
    try {
      const r = await api.post(`/snapshots/${id}/restore`);
      toast.success(`Restored "${r.data.name}"`);
      onOpenChange(false);
      if (typeof onRestored === "function") onRestored(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Restore failed";
      toast.error(msg);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="snapshots-dialog" className="max-w-2xl border-2 border-foreground rounded-none p-0 gap-0">
        <DialogHeader className="border-b-2 border-foreground p-4 space-y-1">
          <DialogTitle className="font-heading font-black uppercase text-2xl inline-flex items-center gap-2">
            <CloudDownload className="w-5 h-5" /> Cloud backups
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Auto-snapshotted on save (1/day per job · last 10 kept). Restore as a new job.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
            </div>
          ) : snapshots.length === 0 ? (
            <div className="py-8 border-2 border-dashed border-foreground/40 text-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              No snapshots yet — save any job to create your first one.
            </div>
          ) : (
            <ul className="divide-y-2 divide-foreground/10">
              {snapshots.map((s) => {
                const when = new Date(s.snapshot_at);
                const label = when.toLocaleString("en-AU", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                });
                const sizeKb = (s.size / 1024).toFixed(1);
                return (
                  <li key={s.id} data-testid="snapshot-row"
                    className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading font-black uppercase text-sm truncate">{s.job_name || "Untitled job"}</div>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                        {label} · {sizeKb} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="snapshot-restore"
                      onClick={() => restore(s.id)}
                      disabled={restoringId === s.id}
                      className="btn-ghost-rugged text-[11px] inline-flex items-center gap-1.5 shrink-0"
                    >
                      {restoringId === s.id ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Restoring…</>
                      ) : (
                        <><RotateCcw className="w-3.5 h-3.5" /> Restore</>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SnapshotsDialog;
